/**
 * Reemplazo de fuentes del Creador (lógica pura): sugiere cómo emparejar las columnas de la
 * hoja actual con las de la nueva, reescribe las referencias del reporte y detecta los campos
 * que quedaron sin equivalente.
 */
import { formulaFieldRefs, renameFormulaFields, tokenizeFormula } from './formulaLanguage';
import { sourceIncludesDataSource } from './filterUtils';

/** Nombre comparable: minúsculas, sin acentos, sin espacios ni signos. */
export function normalizeColumnName(name) {
    return String(name || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]/gu, '');
}

function bigrams(text) {
    const list = [];
    for (let i = 0; i < text.length - 1; i += 1) list.push(text.slice(i, i + 2));
    return list;
}

/** Similitud de Dice sobre bigramas de los nombres normalizados (0 a 1). */
function similarity(a, b) {
    const left = bigrams(normalizeColumnName(a));
    const right = bigrams(normalizeColumnName(b));
    if (!left.length || !right.length) return 0;
    const pool = [...right];
    let common = 0;
    left.forEach((gram) => {
        const idx = pool.indexOf(gram);
        if (idx >= 0) {
            common += 1;
            pool.splice(idx, 1);
        }
    });
    return (2 * common) / (left.length + right.length);
}

const SUGGEST_THRESHOLD = 0.4;

/**
 * Empareja las columnas que usa el reporte con las de la hoja nueva: primero nombres
 * idénticos, luego iguales al normalizar y por último parecidos del mismo tipo.
 * @param {Array<{name, inferred_type?}>} used columnas actuales que usa el reporte
 * @param {Array<{name, inferred_type}>} next columnas de la hoja nueva
 * @returns {{ rows: Array<{from, to, status}>, added: string[] }}
 */
export function suggestColumnMapping(used = [], next = []) {
    const taken = new Set();
    const result = new Map();
    const free = () => next.filter(col => !taken.has(col.name));

    used.forEach((col) => {
        const hit = free().find(item => item.name === col.name);
        if (hit) {
            taken.add(hit.name);
            result.set(col.name, { from: col.name, to: hit.name, status: 'exact' });
        }
    });
    used.forEach((col) => {
        if (result.has(col.name)) return;
        const key = normalizeColumnName(col.name);
        const hit = free().find(item => normalizeColumnName(item.name) === key);
        if (hit) {
            taken.add(hit.name);
            result.set(col.name, { from: col.name, to: hit.name, status: 'normalized' });
        }
    });
    /* Parecidos: se asigna primero el par de mayor similitud. */
    const sameType = (a, b) => !a.inferred_type || !b.inferred_type || a.inferred_type === b.inferred_type;
    const pairs = [];
    used.filter(col => !result.has(col.name)).forEach((col) => {
        free().forEach((item) => {
            if (!sameType(col, item)) return;
            const score = similarity(col.name, item.name);
            if (score >= SUGGEST_THRESHOLD) pairs.push({ col, item, score });
        });
    });
    pairs.sort((a, b) => b.score - a.score).forEach(({ col, item }) => {
        if (result.has(col.name) || taken.has(item.name)) return;
        taken.add(item.name);
        result.set(col.name, { from: col.name, to: item.name, status: 'suggested' });
    });

    const rows = used.map(col => result.get(col.name) || { from: col.name, to: null, status: 'missing' });
    return { rows, added: next.filter(col => !taken.has(col.name)).map(col => col.name) };
}

/* ------------------------------------------------------------------ */
/* Referencias a columnas en el reporte                                */
/* ------------------------------------------------------------------ */

const clone = value => JSON.parse(JSON.stringify(value));

/** Campos que nombra una fórmula: con knownNames usa formulaFieldRefs; si no, [campos] e identificadores. */
function formulaRefs(formula, knownNames = []) {
    if (knownNames.length) return formulaFieldRefs(formula, knownNames);
    const refs = [];
    tokenizeFormula(formula).forEach((tok) => {
        if (tok.type === 'field') refs.push(tok.name.trim());
        if (tok.type === 'ident') refs.push(tok.name);
    });
    return refs;
}

function widgetCalcNames(widget) {
    const query = widget?.query || {};
    return [...(query.row_calculated_fields || []), ...(query.agg_calculated_fields || [])].map(item => item.name);
}

/**
 * Columnas de la hoja que referencia un elemento (consulta, filtros fijos, KPI y fórmulas de
 * sus calculados), sin los nombres de los calculados.
 * @param {object} widget
 * @param {string[]} knownNames nombres posibles para leer fórmulas con nombres de varias palabras
 */
export function widgetColumnRefs(widget, knownNames = []) {
    const query = widget?.query || {};
    const refs = new Set();
    const add = name => { if (name) refs.add(String(name)); };
    (query.dimensions || []).forEach(add);
    Object.entries(query.metrics || {}).forEach(([name, spec]) => add(spec && typeof spec === 'object' && spec.column ? spec.column : name));
    /* Las métricas visibles que son claves de metrics ya cuentan por su columna. */
    (query.visibleMetrics || []).filter(name => !Object.prototype.hasOwnProperty.call(query.metrics || {}, name)).forEach(add);
    Object.keys(query.dateGrains || {}).forEach(add);
    (query.fixedFilters || []).forEach(item => add(item.column));
    add(query.kpi?.dateField);
    add(query.kpi?.targetColumn);
    [...(query.row_calculated_fields || []), ...(query.agg_calculated_fields || [])]
        .forEach(item => formulaRefs(item.formula, knownNames).forEach(add));
    const calcs = new Set(widgetCalcNames(widget));
    return [...refs].filter(name => !calcs.has(name));
}

function catalogOf(report) {
    return { dataSources: report.dataSources || [], joins: report.joins || [] };
}

/** ¿El elemento (o filtro) con esta fuente lee de sourceId? Sin fuente propia = la primera del reporte. */
function readsFrom(ownSourceId, sourceId, report) {
    const own = ownSourceId || report.dataSources?.[0]?.id;
    return sourceIncludesDataSource(own, sourceId, catalogOf(report));
}

function eachWidget(widgets, visit, parent = null) {
    (widgets || []).forEach((widget) => {
        visit(widget, parent);
        if (widget.children?.widgets) eachWidget(widget.children.widgets, visit, widget);
    });
}

/**
 * Dónde se usa cada columna de la fuente: elementos, calculados del reporte, filtros de página,
 * filtros de la fuente y claves de unión.
 * @param {object} report { pages, calculatedFields, dataSources, joins }
 * @param {string} sourceId
 * @param {string[]} columns nombres de columnas de la fuente (para leer fórmulas)
 * @returns {Record<string, Array<{kind, pageId?, pageName?, id, label}>>}
 */
export function collectColumnUsage(report, sourceId, columns = []) {
    const usage = {};
    const push = (name, entry) => {
        if (!name) return;
        (usage[name] = usage[name] || []).push(entry);
    };
    const calcByName = new Map((report.calculatedFields || []).map(item => [item.name, item]));
    const calcUsers = new Set();
    (report.pages || []).forEach((page) => {
        eachWidget(page.widgets, (widget) => {
            if (!widget.query || !readsFrom(widget.query.dataSourceId, sourceId, report)) return;
            const refs = widgetColumnRefs(widget, columns);
            if (!widgetHasRefs(widget)) return;
            refs.forEach(name => push(name, { kind: 'widget', pageId: page.id, pageName: page.name, id: widget.id, label: widget.title || widget.type }));
            widgetCalcNames(widget).forEach(name => calcUsers.add(name));
        });
        (page.filters || []).forEach((filter) => {
            if (filter.column && readsFrom(filter.dataSourceId, sourceId, report)) {
                push(filter.column, { kind: 'filter', pageId: page.id, pageName: page.name, id: filter.id, label: filter.label || filter.column });
            }
        });
    });
    calcByName.forEach((calc) => {
        if (!calcUsers.has(calc.name)) return;
        formulaRefs(calc.formula, columns).forEach(name => push(name, { kind: 'calc', id: calc.id, label: calc.name }));
    });
    const source = (report.dataSources || []).find(item => item.id === sourceId);
    (source?.filters || []).forEach(item => push(item.column, { kind: 'sourceFilter', id: item.id, label: item.column }));
    (report.joins || []).forEach((join) => {
        (join.keys || []).forEach((key) => {
            if (join.leftDataSourceId === sourceId) push(key.left, { kind: 'join', id: join.id, label: 'Unión' });
            if (join.rightDataSourceId === sourceId) push(key.right, { kind: 'join', id: join.id, label: 'Unión' });
        });
    });
    return usage;
}

function widgetHasRefs(widget) {
    const query = widget.query || {};
    return Boolean((query.dimensions || []).length || Object.keys(query.metrics || {}).length
        || (query.agg_calculated_fields || []).length || (query.fixedFilters || []).length);
}

/* ------------------------------------------------------------------ */
/* Reescritura                                                         */
/* ------------------------------------------------------------------ */

function renameIn(map, name) {
    return Object.prototype.hasOwnProperty.call(map, name) ? map[name] : name;
}

function renameList(list, map) {
    return (list || []).map(name => renameIn(map, name)).filter((name, idx, all) => all.indexOf(name) === idx);
}

/** Renombra claves de un objeto; si la clave nueva ya existía se conserva la existente. */
function renameKeys(obj, map) {
    if (!obj || typeof obj !== 'object') return obj;
    const out = {};
    Object.keys(obj).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(map, key)) out[key] = obj[key];
    });
    Object.keys(obj).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(map, key)) return;
        const next = map[key];
        if (!Object.prototype.hasOwnProperty.call(out, next)) out[next] = obj[key];
    });
    return out;
}

function renameWidget(widget, map) {
    const query = widget.query;
    if (!query) return widget;
    const used = new Set(widgetColumnRefs(widget, Object.keys(map)));
    const hadKey = (name) => (query.dimensions || []).includes(name)
        || Object.prototype.hasOwnProperty.call(query.metrics || {}, name)
        || (query.visibleMetrics || []).includes(name);
    const aliases = renameKeys(query.aliases || {}, map);
    /* Títulos: una columna renombrada sin alias conserva su nombre viejo como alias, salvo que
       el nombre nuevo ya existiera en el elemento (sería el título de otra columna). */
    Object.entries(map).forEach(([from, to]) => {
        if (!used.has(from) || from === to) return;
        if (query.aliases?.[from] || hadKey(to) || aliases[to]) return;
        aliases[to] = from;
    });
    const metrics = {};
    Object.entries(query.metrics || {}).forEach(([name, spec]) => {
        const key = renameIn(map, name);
        const nextSpec = spec && typeof spec === 'object' && spec.column ? { ...spec, column: renameIn(map, spec.column) } : spec;
        if (!Object.prototype.hasOwnProperty.call(metrics, key) || !Object.prototype.hasOwnProperty.call(map, name)) metrics[key] = nextSpec;
    });
    const renameCalc = item => ({
        ...item,
        formula: renameFormulaFields(item.formula, map),
        ...(item.aggregations ? { aggregations: renameKeys(item.aggregations, map) } : {}),
    });
    const nextQuery = {
        ...query,
        dimensions: renameList(query.dimensions, map),
        metrics,
        visibleMetrics: renameList(query.visibleMetrics, map),
        dateGrains: renameKeys(query.dateGrains, map),
        aliases,
        metricFormats: renameKeys(query.metricFormats, map),
        totalAggs: renameKeys(query.totalAggs, map),
        totalFormulas: renameKeys(query.totalFormulas, map),
        conditionalFormats: renameKeys(query.conditionalFormats, map),
        columnWidths: renameKeys(query.columnWidths, map),
        fixedFilters: (query.fixedFilters || []).map(item => ({ ...item, column: renameIn(map, item.column) })),
        row_calculated_fields: (query.row_calculated_fields || []).map(renameCalc),
        agg_calculated_fields: (query.agg_calculated_fields || []).map(renameCalc),
    };
    if (query.kpi) {
        nextQuery.kpi = {
            ...query.kpi,
            dateField: query.kpi.dateField && renameIn(map, query.kpi.dateField),
            targetColumn: query.kpi.targetColumn && renameIn(map, query.kpi.targetColumn),
        };
    }
    Object.keys(nextQuery).forEach((key) => {
        if (nextQuery[key] === undefined && query[key] === undefined) delete nextQuery[key];
    });
    ['metricFormats', 'totalAggs', 'totalFormulas', 'conditionalFormats', 'columnWidths', 'dateGrains', 'aliases'].forEach((key) => {
        if (query[key] === undefined && nextQuery[key] && !Object.keys(nextQuery[key]).length) delete nextQuery[key];
    });
    ['fixedFilters', 'row_calculated_fields', 'agg_calculated_fields', 'dimensions', 'visibleMetrics'].forEach((key) => {
        if (query[key] === undefined && Array.isArray(nextQuery[key]) && !nextQuery[key].length) delete nextQuery[key];
    });
    const style = widget.style
        ? {
            ...widget.style,
            ...(widget.style.sortKey ? { sortKey: renameIn(map, widget.style.sortKey) } : {}),
            ...(widget.style.comboLineMetrics ? { comboLineMetrics: renameList(widget.style.comboLineMetrics, map) } : {}),
            ...(widget.style.seriesColors ? { seriesColors: renameKeys(widget.style.seriesColors, map) } : {}),
        }
        : widget.style;
    return { ...widget, query: nextQuery, ...(style !== undefined ? { style } : {}) };
}

/**
 * Aplica un emparejamiento de columnas al reporte (no muta la entrada).
 * Modo fuente (sin widgetId): renombra en todos los elementos, filtros, calculados, filtros de
 * fuente, columnas elegidas y uniones que leen de sourceId; sourcePatch cambia su archivo/pestaña.
 * Modo elemento (con widgetId): renombra solo ese elemento (y sus hijos) y lo pasa a targetSourceId.
 * @param {object} report { pages, calculatedFields, dataSources, joins }
 * @param {{ sourceId: string, mapping: Record<string,string>, widgetId?: string, targetSourceId?: string, sourcePatch?: object }} options
 */
export function applyColumnMapping(report, { sourceId, mapping = {}, widgetId, targetSourceId, sourcePatch } = {}) {
    const out = clone(report);
    const map = Object.fromEntries(Object.entries(mapping || {}).filter(([from, to]) => from && to && from !== to));

    if (widgetId) {
        const target = (out.dataSources || []).find(item => item.id === targetSourceId);
        const convert = (widget) => {
            const renamed = renameWidget(widget, map);
            const children = renamed.children?.widgets
                ? { ...renamed.children, widgets: renamed.children.widgets.map(convert) }
                : renamed.children;
            const query = renamed.query
                ? { ...renamed.query, dataSourceId: targetSourceId, spreadsheet_id: target?.spreadsheet_id, sheet_name: target?.sheet_name }
                : renamed.query;
            return { ...renamed, query, ...(children ? { children } : {}) };
        };
        const walk = list => (list || []).map((widget) => {
            if (widget.id === widgetId) return convert(widget);
            if (widget.children?.widgets) return { ...widget, children: { ...widget.children, widgets: walk(widget.children.widgets) } };
            return widget;
        });
        out.pages = (out.pages || []).map((page) => {
            let filterId = null;
            eachWidget(page.widgets, (widget) => { if (widget.id === widgetId) filterId = widget.filterId || null; });
            return {
                ...page,
                widgets: walk(page.widgets),
                filters: (page.filters || []).map(filter => (filterId && filter.id === filterId
                    ? { ...filter, column: renameIn(map, filter.column), dataSourceId: targetSourceId }
                    : filter)),
            };
        });
        return out;
    }

    /* Calculados del reporte: se reescriben si ningún elemento de otra fuente los usa. */
    const foreignCalcs = new Set();
    (report.pages || []).forEach(page => eachWidget(page.widgets, (widget) => {
        if (widget.query && !readsFrom(widget.query.dataSourceId, sourceId, report)) widgetCalcNames(widget).forEach(name => foreignCalcs.add(name));
    }));

    const walk = list => (list || []).map((widget) => {
        let next = widget;
        if (widget.query && widgetHasRefs(widget) && readsFrom(widget.query.dataSourceId, sourceId, report)) {
            next = renameWidget(widget, map);
        }
        if (next.query && sourcePatch && (next.query.dataSourceId || report.dataSources?.[0]?.id) === sourceId && widgetHasRefs(widget)) {
            next = { ...next, query: { ...next.query, spreadsheet_id: sourcePatch.spreadsheet_id, sheet_name: sourcePatch.sheet_name } };
        }
        if (next.children?.widgets) next = { ...next, children: { ...next.children, widgets: walk(next.children.widgets) } };
        return next;
    });
    out.pages = (out.pages || []).map(page => ({
        ...page,
        widgets: walk(page.widgets),
        filters: (page.filters || []).map(filter => (readsFrom(filter.dataSourceId, sourceId, report)
            ? { ...filter, column: renameIn(map, filter.column) }
            : filter)),
    }));
    out.calculatedFields = (out.calculatedFields || []).map(calc => (foreignCalcs.has(calc.name)
        ? calc
        : {
            ...calc,
            formula: renameFormulaFields(calc.formula, map),
            ...(calc.aggregations ? { aggregations: renameKeys(calc.aggregations, map) } : {}),
        }));
    out.dataSources = (out.dataSources || []).map((source) => {
        if (source.id !== sourceId) return source;
        const next = {
            ...source,
            ...(sourcePatch || {}),
            filters: (source.filters || []).map(item => ({ ...item, column: renameIn(map, item.column) })),
        };
        if (source.selectedColumns) {
            next.selectedColumns = {
                ...source.selectedColumns,
                dimensions: renameList(source.selectedColumns.dimensions, map),
                metrics: renameList(source.selectedColumns.metrics, map),
                order: renameList(source.selectedColumns.order, map),
            };
        }
        return next;
    });
    out.joins = (out.joins || []).map(join => ({
        ...join,
        keys: (join.keys || []).map(key => ({
            left: join.leftDataSourceId === sourceId ? renameIn(map, key.left) : key.left,
            right: join.rightDataSourceId === sourceId ? renameIn(map, key.right) : key.right,
        })),
    }));
    return out;
}

/**
 * Elementos y filtros de página que referencian columnas que no están en el esquema cargado de
 * su fuente. Fuentes sin esquema (o con columnas vacías) no se evalúan.
 * @returns {Array<{kind, pageId, pageName, id, label, fields: string[]}>}
 */
export function findMissingFields(report, schemasBySource = {}, calcNames = []) {
    const calcs = new Set(calcNames);
    const columnsOf = (ownSourceId) => {
        const id = ownSourceId || report.dataSources?.[0]?.id;
        const cols = schemasBySource[id]?.columns;
        return cols && cols.length ? cols.map(col => col.name) : null;
    };
    const items = [];
    (report.pages || []).forEach((page) => {
        eachWidget(page.widgets, (widget) => {
            if (!widget.query || !widgetHasRefs(widget)) return;
            const cols = columnsOf(widget.query.dataSourceId);
            if (!cols) return;
            const known = new Set(cols);
            const fields = widgetColumnRefs(widget, [...cols, ...calcs])
                .filter(name => !calcs.has(name) && !known.has(name));
            if (fields.length) items.push({ kind: 'widget', pageId: page.id, pageName: page.name, id: widget.id, label: widget.title || widget.type, fields });
        });
        (page.filters || []).forEach((filter) => {
            if (!filter.column) return;
            const cols = columnsOf(filter.dataSourceId);
            if (!cols || cols.includes(filter.column) || calcs.has(filter.column)) return;
            items.push({ kind: 'filter', pageId: page.id, pageName: page.name, id: filter.id, label: filter.label || filter.column, fields: [filter.column] });
        });
    });
    return items;
}

/** Fuente de un elemento; los elementos viejos sin fuente propia leen de la primera del reporte. */
export function widgetSourceId(widget, dataSources = []) {
    return widget?.query?.dataSourceId || dataSources[0]?.id;
}

/** Ids de las fuentes de hoja cuyo archivo o pestaña cambió entre dos catálogos (p. ej. al deshacer). */
export function changedSheetSourceIds(before = [], after = []) {
    return after
        .filter((source) => {
            if (source.kind === 'join') return false;
            const prev = before.find(item => item.id === source.id);
            return !prev || prev.spreadsheet_id !== source.spreadsheet_id || prev.sheet_name !== source.sheet_name;
        })
        .map(source => source.id);
}
